import { flatten, memoize, uniqueId } from "lodash-es";
import { elt } from '../../utils/browser';
import CheckWorker from './CodeMirrorJsErrorChecker.worker?worker'; // use .worker?worker to support both webpack & vite
import { formatCodeWithMonaco } from "../../../lib/utils/monaco";
import { getMonaco } from "../../../lib/utils/monacoProvider";
const getWorker = memoize(() => typeof CheckWorker === 'function' ? new CheckWorker() : null);
function callWorker(command, params) {
  return new Promise((resolve, reject) => {
    const reqId = uniqueId();
    const worker = getWorker();
    if (!worker) return reject(new Error('Worker not available'));
    function listener({
      data
    }) {
      if ((data === null || data === void 0 ? void 0 : data.ucMsgSource) !== 'workerThread') return;
      if (data.reqId !== reqId) return;
      if (data.error) reject(new Error(data.error));else resolve(data.result);
      worker.removeEventListener('message', listener, false);
    }
    worker.addEventListener('message', listener, false);
    worker.postMessage({
      reqId,
      ucMsgSource: 'mainThread',
      command,
      ...params
    });
  });
}
const analyze = parts => callWorker('analyze', {
  parts
});
const format = async parts => {
  const formatWithMonaco = async () => {
    await getMonaco();
    const ans = {
      parts: new Array(parts.length)
    };
    await Promise.all(parts.map(async (p, idx) => {
      const out = await formatCodeWithMonaco(p.text);
      ans.parts[idx] = {
        from: p.from,
        text: out,
        oldLength: p.text.length
      };
    }));
    // console.log('Powered by monaco');
    return ans;
  };
  const mResult = await Promise.race([formatWithMonaco().catch(err => {
    console.error('[UICore] Failed to format with monaco', err);
    return null;
  }), new Promise(r => setTimeout(r, 500)).then(() => null)]);
  // monaco 执行失败或者超时，使用 astring 格式化
  if (!mResult) return callWorker('format', {
    parts
  });
  return mResult;
};
// 这个元素是绝对定位放在错误字符的下面的
function createErrorMarker() {
  return elt('span', {
    class: 'uc-anim-blinking ucd-exprInput-errorWidget'
  }, []);
}
const errorLineClassName = 'ucd-exprInput-errorLine';
export class CodeMirrorJsErrorChecker {
  constructor(root, cm) {
    this.root = root;
    this.cm = cm;
    this.widgets = [];
    this.lastMarkedLineHandle = [];
  }
  async check(parts) {
    try {
      const {
        cm
      } = this;
      const result = await analyze(parts);
      const errLineNos = new Set();
      const errors = flatten(result.parts.map(it => it.errors));
      if (this.widgets.length < errors.length) {
        const length = errors.length - this.widgets.length;
        this.widgets = this.widgets.concat(Array.from({
          length
        }, createErrorMarker));
      } else {
        this.widgets.splice(errors.length).forEach(el => el.remove());
      }
      errors.forEach((it, i) => {
        const pos = cm.posFromIndex(it.pos);
        cm.addWidget(pos, this.widgets[i]);
        errLineNos.add(pos.line);
      });
      // ----
      this.lastMarkedLineHandle = this.lastMarkedLineHandle.filter(line => {
        const num = cm.getLineNumber(line);
        if (errLineNos.has(num)) {
          errLineNos.delete(num);
          return true;
        }
        cm.removeLineClass(line, 'background', errorLineClassName);
        return false;
      });
      errLineNos.forEach(num => {
        const line = cm.addLineClass(num, 'background', errorLineClassName);
        this.lastMarkedLineHandle.push(line);
      });
      // ----
      if (errors.length) this.root.$el.classList.add('hasError');else this.root.$el.classList.remove('hasError');
      return result;
    } catch (err) {
      // ignore
    }
  }
  async format(parts) {
    const {
      cm
    } = this;
    const result = await format(parts);
    let text = cm.getValue();
    result.parts.sort((a, b) => b.from - a.from).forEach(p => {
      const front = text.slice(0, p.from);
      const end = text.slice(p.from + p.oldLength);
      const code = p.text.replace(/^\s+/, '').replace(/\s+$/, '');
      let leading = '';
      if (front.endsWith('{{') && end.startsWith('}}')) {
        leading = code.includes('\n') ? '\n' : ' ';
      }
      text = front + leading + code + leading + end;
    });
    return text;
  }
}
//# sourceMappingURL=CodeMirrorJsErrorChecker.js.map
